import { useId } from 'react';
import { CaretDoubleLeftIcon } from '@phosphor-icons/react/dist/csr/CaretDoubleLeft';
import { CaretLeftIcon } from '@phosphor-icons/react/dist/csr/CaretLeft';
import { CaretRightIcon } from '@phosphor-icons/react/dist/csr/CaretRight';
import { useTranslation } from 'react-i18next';

import { Button } from '../../primitives/Button/Button';
import { Select, type SelectOption } from '../../primitives/Select/Select';
import { Surface } from '../../primitives/Surface/Surface';
import type { CursorPages } from './useCursorPages';
import styles from './Pagination.module.css';

/**
 * `Pagination` — the one control every list screen adopts (task plan, File
 * Structure). It is a pure renderer over `useCursorPages`'s state: every
 * click calls straight into `pages.next()` / `pages.prev()` / `pages.first()`
 * / `pages.setPerPage()`, and every number on screen — the range, the total —
 * comes from `total`/`rowsOnPage`, which the CALLER reads out of the latest
 * response's `meta`. This component holds no fetch state of its own.
 *
 * ## Rendered TWICE on every list screen, and `instanceLabel`
 *
 * Handoff-driven layouts put one copy above the table and one below. Six
 * identical control names tabbed twice in a row — "First page", "First
 * page", "Previous page", "Previous page", ... — is indistinguishable to a
 * screen-reader user with no way to tell which copy has focus. `instanceLabel`
 * is how a caller disambiguates: pass a short, already-translated position
 * word ("Top", "Bottom") and every control's ACCESSIBLE NAME carries it, e.g.
 * "First page — Top" / "First page — Bottom". Omitting it (a screen with one
 * instance, or a test) leaves the bare name.
 *
 * `instanceLabel` names exactly what the prop is: literal disambiguating TEXT
 * appended to an accessible name — never an element id for `aria-labelledby`
 * (the convention `DataTable` and `Dialog` use their own `-By`-suffixed ids
 * for; this prop deliberately does not borrow that suffix, so it cannot be
 * misread as one). Critically, this text reaches assistive technology ONLY —
 * the row-count select keeps a plain, unsuffixed `label` (a real, VISIBLE
 * `<label>` per `Field.tsx`) and carries the suffix on `aria-label` instead,
 * so a caller never prints "Rows per page — Bottom" on screen. The three icon
 * buttons have no visible label element at all — `aria-label` is the only
 * text they ever have — so for them the suffixed string simply *is* the
 * (invisible) accessible name.
 *
 * ## Numbers are formatted here, not by i18next interpolation
 *
 * The handoff's numerals rule ("Western digits in data columns for
 * sortability") already holds for plain `{{n}}` interpolation with no extra
 * work — i18next never reformats a value that was not given an explicit
 * `number` formatter. Grouping ("1,284") is the one thing plain interpolation
 * does not do, so `formatCount` supplies it — pinned to `en-US` so the digits
 * and the separator are identical in both locales, exactly as `list.range`'s
 * own test (`i18n.test.ts`) already requires for this shape of value.
 *
 * ## The range's upper bound comes from `meta.total`/`perPage`, never from
 * `rowsOnPage`
 *
 * `rowsOnPage` is only ever the RENDERED row count — a fact about this
 * render, not about the server's page. While a request is in flight it is
 * still the *previous* response's count, and an optimistic client-side row
 * removal shifts it with no request at all. The upper bound must come from
 * the same source as the lower bound (`pageIndex`/`perPage`), clamped to
 * `total` for a short last page: `Math.min(from + perPage - 1, total)`.
 * `rowsOnPage` is used only to decide whether to render the range line at
 * all (an empty page has none) and, when `total` is `null` (an endpoint that
 * omits it), as the only source left to bound the range with.
 */

export interface PaginationProps {
  pages: CursorPages;
  /** From the latest response's `meta.total`. `null` when the endpoint omits it. */
  total: number | null;
  /** How many rows are actually rendered on the current page. */
  rowsOnPage: number;
  /** A short, translated position word disambiguating this instance's controls. */
  instanceLabel?: string;
}

const PAGE_SIZES = [10, 25, 50, 100, 200] as const;

function formatCount(n: number): string {
  return new Intl.NumberFormat('en-US').format(n);
}

/** `${base} — ${suffix}` when a suffix is given, `base` unchanged otherwise. */
function withPosition(base: string, suffix: string | undefined): string {
  return suffix === undefined || suffix === '' ? base : `${base} — ${suffix}`;
}

export function Pagination({ pages, total, rowsOnPage, instanceLabel }: PaginationProps) {
  const { t } = useTranslation();
  const uid = useId();

  const sizeOptions: readonly SelectOption[] = PAGE_SIZES.map((size) => ({
    value: String(size),
    label: String(size),
  }));

  const from = pages.pageIndex * pages.perPage + 1;
  // See the file docblock: the upper bound is derived from `total`/`perPage`
  // whenever `total` is known, and falls back to `rowsOnPage` only when the
  // endpoint gives no total at all.
  const to = total === null ? from + rowsOnPage - 1 : Math.min(from + pages.perPage - 1, total);
  const hasRange = rowsOnPage > 0;

  const rowsPerPageLabel = t('pagination.rowsPerPage');
  const suffixedRowsPerPageLabel = withPosition(rowsPerPageLabel, instanceLabel);

  return (
    <Surface
      className={styles['pagination']}
      // A `nav` LANDMARK, not a `div` — see the file docblock: `aria-label`
      // disambiguates it from every other unnamed `navigation` role on the
      // page. `Surface` supplies the opaque ground (spec D-3); this control
      // is rendered directly on the page ground in over half its call sites
      // (`CaseList`, `AuditLogScreen`, `EscalationList`, ... — see
      // Pagination.contrast.test.ts's own `GROUNDS` comment) with no card of
      // its own until now, which left its text sitting on `bg.gif` there.
      as="nav"
      aria-label={withPosition(t('pagination.landmark'), instanceLabel)}
    >
      {hasRange ? (
        <p className={styles['range']} data-testid="paginationRange">
          {total === null
            ? t('pagination.rangeNoTotal', { from: formatCount(from), to: formatCount(to) })
            : t('pagination.range', {
                from: formatCount(from),
                to: formatCount(to),
                total: formatCount(total),
              })}
        </p>
      ) : null}

      <Select
        id={`${uid}-rows-per-page`}
        label={rowsPerPageLabel}
        // Suffix reaches the accessible name ONLY — see the file docblock.
        // `aria-label` overrides the label association for accessible-name
        // computation while leaving the visible `<label>` text unsuffixed.
        aria-label={suffixedRowsPerPageLabel === rowsPerPageLabel ? undefined : suffixedRowsPerPageLabel}
        width="compact"
        // Label beside the select, not above it: one line, so the pagination
        // card is as short as its buttons.
        className={styles['perPage']}
        options={sizeOptions}
        value={String(pages.perPage)}
        onChange={(event) => {
          pages.setPerPage(Number(event.target.value));
        }}
      />

      <div className={styles['controls']}>
        <Button
          variant="icon"
          aria-label={withPosition(t('pagination.first'), instanceLabel)}
          disabled={!pages.canPrev}
          onClick={pages.first}
          icon={<CaretDoubleLeftIcon size={16} className={styles['caret']} />}
        />
        <Button
          variant="icon"
          aria-label={withPosition(t('pagination.previous'), instanceLabel)}
          disabled={!pages.canPrev}
          onClick={pages.prev}
          icon={<CaretLeftIcon size={16} className={styles['caret']} />}
        />
        <Button
          variant="icon"
          aria-label={withPosition(t('pagination.next'), instanceLabel)}
          disabled={!pages.canNext}
          onClick={pages.next}
          icon={<CaretRightIcon size={16} className={styles['caret']} />}
        />
      </div>
    </Surface>
  );
}
